import { useMemo, useState } from "react";
import { ICONES, Icone, corAvatar, tinta } from "../visual.jsx";
import { useCelular, useEstado } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { Avatar, Contador, MenuDeAcoes, contagem } from "./comuns.jsx";
import { SEM_CRIATIVO } from "../criativos.js";
import { cn } from "@/lib/utils";

/**
 * O valor que a URL usa para "ninguém": a fila é uma linha de verdade, e
 * `?resp=` vazio significaria "sem recorte". MESMO literal de `SEM_RESPONSAVEL`
 * em src/leads.ts — os dois lados leem a mesma URL.
 */
export const SEM_RESPONSAVEL = "__fila__";

/*
 * A PÍLULA do filtro: 32 de altura, recuo 14, 13/600, o número numa cápsula de
 * 20 — a mesma pele das `Fichas` da casa, escrita aqui porque estas pastilhas
 * não são uma escolha única (cada uma liga e desliga sozinha, `aria-pressed`) e
 * o `ToggleGroup` das Fichas exige uma marcada por vez. `alvo` dá os 40/44 de
 * toque sem crescer o desenho: eram 26px, menores que a ponta do dedo, na
 * pastilha que muda o recorte da mesa inteira. Ligada é tinta cheia.
 */
const PILULA =
  "group alvo flex h-8 flex-none cursor-pointer touch-manipulation items-center gap-2 rounded-full border border-borda-controle bg-card px-3.5 text-[13px] font-semibold whitespace-nowrap text-tinta-media hover:bg-foreground/4 hover:text-foreground aria-pressed:border-tinta aria-pressed:bg-tinta aria-pressed:text-mesa aria-pressed:hover:bg-tinta/86 aria-pressed:hover:text-mesa";

/* A cápsula do número dentro da pílula; na ligada ela vira véu da mesa. */
const CAPSULA =
  "-mr-1.5 inline-grid h-5 min-w-[22px] place-items-center rounded-full bg-foreground/8 px-1.5 text-selo leading-none font-bold tabular-nums text-tinta-suave group-aria-pressed:bg-mesa/20 group-aria-pressed:text-mesa";

/* O rosto escolhido se diz por um aro de 2px separado do retrato por 2px da cor
   da coluna — em `box-shadow`, e não em `border`: a borda somava 4px ao rosto
   (34 viravam 38) e o aro, igual ao anel de foco, não mexe na medida. */
const ARO_DO_ROSTO = "shadow-[0_0_0_2px_var(--color-secondary),0_0_0_4px_var(--color-foreground)]";
const ARO_DO_HOVER =
  "hover:shadow-[0_0_0_2px_var(--color-secondary),0_0_0_4px_color-mix(in_oklab,var(--color-foreground)_20%,transparent)]";

/**
 * O filtro da mesa — versão "Equipe + funil" (mockup A, escolhido em 26/08).
 *
 * Eram sete pastilhas e uma grade escondida num balão que, de quebra, não
 * abria. Agora é UM filtro, sempre à vista, que responde às duas perguntas do
 * dia: DE QUEM é (a fileira de rostos, com a carga de cada um; a fila é o
 * círculo tracejado) e EM QUE ETAPA está (o funil como barra colorida, cada
 * segmento na proporção de casos). Toque num rosto, num segmento, ou nos dois;
 * o recorte fica ESCRITO logo abaixo, com o ✕ — e mora na URL (`?etapa=`,
 * `?resp=`), então "os casos da Camila em aguardando documentos" se manda por
 * mensagem.
 *
 * Os números vêm do banco (`matrizDaMesa`, em src/leads.ts), contados sobre a
 * busca atual — nunca da página carregada. Sobrou uma pastilha só, "esperando
 * resposta": é a fila do dia, e não cabe em rosto nem em etapa.
 */
export default function FiltroDaMesa() {
  const { matriz, etapas, equipe, contadores, catalogoDeTags } = useEstado();
  const celular = useCelular();
  const { etapaDaMesa, respDaMesa, tagDaMesa, criativoDaMesa, filtro, ajustar } = useNavegacao();

  const { pessoas, segmentos, noRecorte, rostosPorEtapa } = useMemo(() => {
    const porCelula = new Map(matriz.map((c) => [`${c.stage}|${c.resp}`, c.n]));
    const porResp = new Map();
    const porEtapa = new Map();
    for (const c of matriz) {
      porResp.set(c.resp, (porResp.get(c.resp) ?? 0) + c.n);
      porEtapa.set(c.stage, (porEtapa.get(c.stage) ?? 0) + c.n);
    }
    const respChave = respDaMesa === SEM_RESPONSAVEL ? "" : respDaMesa;
    const ativos = new Map((equipe ?? []).map((p) => [p.nome.toUpperCase(), p]));
    /* Quem aparece: toda pessoa ATIVA do cadastro (mesmo com zero — carga zero é
       informação) mais quem tem caso e não está no cadastro, mais a fila. */
    const nomes = new Set([...ativos.keys(), ...[...porResp.keys()].filter(Boolean)]);
    const pessoas = [...nomes]
      .map((nome) => {
        const p = ativos.get(nome);
        const n = etapaDaMesa ? (porCelula.get(`${etapaDaMesa}|${nome}`) ?? 0) : (porResp.get(nome) ?? 0);
        return { chave: nome, nome: p?.nome ?? nome, foto: p?.avatar, cor: p?.cor, n, total: porResp.get(nome) ?? 0 };
      })
      .sort((a, b) => b.total - a.total || a.nome.localeCompare(b.nome));
    const fila = {
      chave: SEM_RESPONSAVEL,
      nome: "Fila",
      n: etapaDaMesa ? (porCelula.get(`${etapaDaMesa}|`) ?? 0) : (porResp.get("") ?? 0),
    };
    const segmentos = (etapas ?? []).map((e) => ({
      ...e,
      n: respDaMesa ? (porCelula.get(`${e.chave}|${respChave}`) ?? 0) : (porEtapa.get(e.chave) ?? 0),
    }));
    const noRecorte = matriz
      .filter((c) => (!etapaDaMesa || c.stage === etapaDaMesa) && (!respDaMesa || c.resp === respChave))
      .reduce((s, c) => s + c.n, 0);
    /* Quem está com caso em cada etapa — os rostos da lista detalhada. */
    const rostosPorEtapa = new Map(
      (etapas ?? []).map((e) => [
        e.chave,
        [fila, ...pessoas]
          .map((p) => ({ ...p, n: porCelula.get(`${e.chave}|${p.chave === SEM_RESPONSAVEL ? "" : p.chave}`) ?? 0 }))
          .filter((p) => p.n > 0),
      ]),
    );
    return { pessoas: [fila, ...pessoas], segmentos, noRecorte, rostosPorEtapa };
  }, [matriz, etapas, equipe, etapaDaMesa, respDaMesa]);

  /* Aberto/fechado é preferência de quem olha, não recorte: fica no navegador,
     não na URL. Sem valor gravado nasce fechado — duas linhas de altura. */
  const [detalhe, setDetalhe] = useState(() => {
    try {
      return localStorage.getItem("mesa.filtro.detalhe") === "1";
    } catch {
      return false;
    }
  });
  const alternarDetalhe = () => {
    setDetalhe((d) => {
      try {
        localStorage.setItem("mesa.filtro.detalhe", d ? "0" : "1");
      } catch {
        /* navegador sem storage: só não lembra */
      }
      return !d;
    });
  };
  const escolherPessoa = (chave) => ajustar({ resp: respDaMesa === chave ? null : chave });
  const escolherEtapa = (chave) => ajustar({ etapa: etapaDaMesa === chave ? null : chave });
  const ativo = Boolean(etapaDaMesa || respDaMesa || tagDaMesa || criativoDaMesa);
  const rotuloEtapa = segmentos.find((e) => e.chave === etapaDaMesa)?.rotulo ?? etapaDaMesa;
  const nomeResp = pessoas.find((p) => p.chave === respDaMesa)?.nome ?? respDaMesa;
  const esperando = contadores["Prazo hoje"] ?? 0;
  const soEsperando = filtro === "Prazo hoje";
  /* O ARQUIVO como pastilha, e não como interruptor escondido: arquivar
     prometia "dá para trazer de volta" e não havia tela nenhuma que o mostrasse
     — a única pista de que o caso existia era ele não estar na lista. Sendo
     recorte, ele ganha contagem, URL (`?filtro=Arquivados`) e matriz de graça,
     como qualquer outro. Só aparece com caso dentro: pastilha que diz zero é
     um controle a mais na fileira mais cheia da mesa. */
  const arquivados = contadores.Arquivados ?? 0;
  const soArquivados = filtro === "Arquivados";
  /*
   * Quantos rostos cabem na fileira sem ela rolar nem quebrar.
   *
   * Sete: a coluna passou a ter 380px (320 no tablet) justamente para caber a
   * equipe do escritório, e cada rosto ocupa 43px com o respiro — sobra o que o
   * "+N" e a pastilha de espera precisam. É medida do desenho, não conta de
   * execução: medir a largura aqui faria a fileira mudar de tamanho enquanto a
   * pessoa arrasta a divisória. Acima de sete pessoas o "+N" continua sendo a
   * rede — ele deixou de ser o caso comum, que era o defeito.
   */
  const TETO_DE_ROSTOS = 7;
  /* O escolhido nunca vai para dentro do menu: esconder o recorte que está
     aplicado é a tela mentindo sobre o que está mostrando. */
  const naFileira = pessoas
    .slice(0, TETO_DE_ROSTOS)
    .concat(
      respDaMesa && !pessoas.slice(0, TETO_DE_ROSTOS).some((p) => p.chave === respDaMesa)
        ? pessoas.filter((p) => p.chave === respDaMesa)
        : [],
    );
  const noMenu = pessoas.filter((p) => !naFileira.includes(p));
  const visiveis = segmentos.filter((e) => e.n > 0 || e.chave === etapaDaMesa);
  const somaVisivel = visiveis.reduce((s, e) => s + e.n, 0) || 1;

  return (
    <div className="mt-2.5">
      {/* A fileira de rostos: a carga de cada um no recorte da etapa escolhida
          (ou total). O número é do banco; "0" fica esmaecido, não some. */}
      {/*
       * UMA LINHA, sempre: nem rolagem, nem quebra.
       *
       * Ela rolava de lado com sete pessoas — e contêiner que rola no eixo X
       * recorta no Y também, então o número da carga (que fica no canto de cima
       * do rosto) aparecia pela metade em TODOS eles. Quebrar em duas linhas
       * resolvia os dois defeitos e criava um terceiro: a altura do topo da mesa
       * mudava conforme o tamanho da equipe, e o que está embaixo (a barra do
       * funil, a lista) descia junto.
       *
       * Então cabe o que cabe — a fila e os quatro de maior carga — e o resto
       * entra num "+N" que abre a lista inteira, com nome e número escritos. Quem
       * está filtrado nunca some para dentro do menu: a tela não pode esconder o
       * recorte que ela mesma está aplicando.
       *
       * `pt-2.5` é o respiro que o número precisa para não ser cortado por quem
       * está em cima.
       */}
      <div role="group" aria-label="Filtrar por responsável" className="flex items-center gap-[9px] pt-2.5 pb-0.5">
        {naFileira.map((p) => {
          const marcado = respDaMesa === p.chave;
          return (
            <button
              key={p.chave}
              onClick={() => escolherPessoa(p.chave)}
              aria-pressed={marcado}
              aria-label={`${p.nome}: ${p.n} ${p.n === 1 ? "atendimento" : "atendimentos"}`}
              data-dica={p.chave === SEM_RESPONSAVEL ? `Fila — ${p.n} sem responsável` : `${p.nome} — ${p.n} no recorte`}
              /* 28, a medida dos rostos em fila. O pulo de 1px no hover saiu: o
                 aro fraco diz "este se toca" sem a fileira tremer sob o ponteiro. */
              className={`relative flex-none cursor-pointer rounded-full border-0 bg-transparent p-0 transition-shadow ${
                marcado ? ARO_DO_ROSTO : ARO_DO_HOVER
              }`}
            >
              {/* A fila não é pessoa: é pílula com o número DENTRO. Como círculo
                  de 28 ela vestia o contador por cima, e com "99+" a palavra
                  "fila" sumia debaixo dele. */}
              {p.chave === SEM_RESPONSAVEL ? (
                <span className="flex h-7 items-center gap-1.5 rounded-full border-[1.5px] border-dashed border-terra/50 pr-1 pl-2.5 text-apoio font-semibold text-terra">
                  Fila
                  <Contador n={p.n} zero tom={p.n ? "terra" : "quieto"} />
                </span>
              ) : (
                <Avatar nome={p.nome} foto={p.foto} cor={p.cor} tamanho={28} />
              )}
              {/* O contador da casa: 20 de altura, "99+", e o zero em tom quieto
                  em vez de 35% de opacidade — carga zero é informação, e tinha de
                  continuar legível. */}
              {p.chave === SEM_RESPONSAVEL ? null : (
                <Contador
                  n={p.n}
                  zero
                  tom={p.n ? "tinta" : "quieto"}
                  classe="absolute -top-2 -right-2 shadow-[0_0_0_2px_var(--color-secondary)]"
                />
              )}
            </button>
          );
        })}
        {/* O que não coube: a lista inteira, com nome e carga escritos — que é
            mais legível que o rosto, e é onde se procura alguém pelo nome. */}
        {noMenu.length > 0 && (
          <MenuDeAcoes
            rotulo={`Mais ${noMenu.length} pessoa(s) da equipe`}
            titulo="Filtrar por quem cuida"
            legenda="Quem cuida"
            largura="240px"
            altura="360px"
            classe="flex size-7 flex-none items-center justify-center rounded-full border border-dashed border-borda-controle text-selo font-bold text-tinta-suave hover:bg-foreground/4 hover:text-foreground data-[popup-open]:bg-foreground/8"
            itens={noMenu.map((p) => ({
              chave: p.chave,
              rotulo: p.nome,
              detalhe: `${p.n} no recorte`,
              marca: respDaMesa === p.chave ? "✓" : undefined,
              aoTocar: () => escolherPessoa(p.chave),
            }))}
          >
            +{noMenu.length}
          </MenuDeAcoes>
        )}

        <div className="min-w-0 flex-1" />

        {(arquivados > 0 || soArquivados) && (
          <button
            onClick={() => ajustar({ filtro: soArquivados ? null : "Arquivados" })}
            aria-pressed={soArquivados}
            aria-label={`Mostrar os ${arquivados} atendimentos arquivados`}
            data-dica={`${arquivados} arquivado(s) — fora da mesa, mas guardados`}
            className={cn(PILULA, "px-2.5")}
          >
            <Icone de={ICONES.arquivar} tamanho={14} />
            <span className={CAPSULA}>{contagem(arquivados)}</span>
          </button>
        )}

        {esperando > 0 && (
          <button
            onClick={() => ajustar({ filtro: soEsperando ? null : "Prazo hoje" })}
            aria-pressed={soEsperando}
            data-dica={`${esperando} esperando resposta do escritório há mais de 15 min`}
            /* Ligada ela é TERRA e não tinta: é a fila de quem espera resposta,
               e terra é a cor da atenção. */
            className={cn(
              PILULA,
              "px-2.5 aria-pressed:border-terra aria-pressed:bg-veu-espera aria-pressed:text-terra aria-pressed:hover:bg-veu-espera aria-pressed:hover:text-terra",
            )}
          >
            <Icone de={ICONES.ampulheta} tamanho={14} />
            <span className={cn(CAPSULA, "group-aria-pressed:bg-terra group-aria-pressed:text-sobre-terra")}>
              {contagem(esperando)}
            </span>
          </button>
        )}
      </div>

      {/*
        No TELEFONE o funil vira fileira de pastilhas ESCRITAS.
        A barra proporcional se lê pela dica (`data-dica`), e dica é hover — que
        em tela de toque não existe. O que sobrava ali eram oito retângulos
        coloridos com um número dentro e nenhuma forma de descobrir qual era
        "Aguardando documentos": o filtro mais usado da mesa virava adivinhação.
        A pastilha diz o nome, cabe no dedo e rola de lado, como a fileira de
        filtros de qualquer aplicativo. A proporção se perde — e ela é o que
        menos importa em 390px de largura.
      */}
      {celular ? (
        <div
          role="group"
          aria-label="Filtrar por etapa"
          /* `py-1.5`: o alvo invisível de 44 passa 6px da pílula de 32, e numa
             fileira que rola de lado o que sobra na vertical vira rolagem. */
          className="-mx-4 mt-1 flex gap-1.5 overflow-x-auto px-4 py-1.5"
        >
          {visiveis.map((e) => {
            const marcado = etapaDaMesa === e.chave;
            const cor = e.cor || corAvatar(e.chave);
            return (
              <button
                key={e.chave}
                onClick={() => escolherEtapa(e.chave)}
                aria-pressed={marcado}
                className={PILULA}
              >
                <span className="size-2 flex-none rounded-full" style={{ background: cor }} />
                <span>{e.rotulo}</span>
                <span className={CAPSULA}>{contagem(e.n)}</span>
              </button>
            );
          })}
          {!visiveis.length && <span className="font-mono text-selo text-tinta-fraca">sem casos neste recorte</span>}
        </div>
      ) : (
        /* O funil: cada etapa na proporção de casos. Cor do esquema (stages.cor) ou
          estável pela chave — valor de dado, logo inline. 24 de altura e raio de
          pílula, o idioma do trilho do processo. */
        <div role="group" aria-label="Filtrar por etapa" className="mt-2.5 flex h-6 gap-[2px] overflow-hidden rounded-full">
          {visiveis.length ? (
            visiveis.map((e) => {
              const marcado = etapaDaMesa === e.chave;
              const cor = e.cor || corAvatar(e.chave);
              return (
                <button
                  key={e.chave}
                  onClick={() => escolherEtapa(e.chave)}
                  aria-pressed={marcado}
                  aria-label={`${e.rotulo}: ${e.n}`}
                  data-dica={`${e.rotulo} — ${e.n}`}
                  /* Marcado e foco moram POR DENTRO, porque a barra é
                     `overflow:hidden` (é o que arredonda as pontas) e o anel de
                     fora era cortado em cima e embaixo. O marcado é o aro de 2px
                     na borda; o foco, o anel 2px mais para dentro — os dois
                     juntos continuam distinguíveis. */
                  className={`relative min-w-[6px] cursor-pointer border-0 p-0 transition-opacity focus-visible:outline-2! focus-visible:-outline-offset-4! focus-visible:outline-anel! ${
                    marcado ? "opacity-100 shadow-[inset_0_0_0_2px_var(--color-foreground)]" : "opacity-85 hover:opacity-100"
                  }`}
                  style={{ flex: Math.max(e.n, 0.6), background: cor }}
                >
                  {e.n / somaVisivel > 0.09 && (
                    <span className="absolute inset-0 flex items-center justify-center font-mono text-selo font-bold text-sobre-escuro">
                      {e.n}
                    </span>
                  )}
                </button>
              );
            })
          ) : (
            <span className="flex-1 rounded-full bg-foreground/8 text-center font-mono text-apoio leading-6 text-tinta-fraca">
              sem casos neste recorte
            </span>
          )}
        </div>
      )}
      {/* Sem legenda fixa: a etapa se lê na dica e na linha "Mostrando". Quem
          quer o mapa inteiro abre o detalhe — a lista de etapas com barra de
          volume e os rostos de quem está em cada uma, que também é a legenda. */}
      <div className={`mt-1 flex justify-end ${celular ? "hidden" : ""}`}>
        <button
          onClick={alternarDetalhe}
          aria-expanded={detalhe}
          aria-controls="detalhe-do-funil"
          className="alvo flex cursor-pointer items-center gap-1 rounded-etiqueta border-0 bg-transparent p-0 py-0.5 text-selo font-semibold text-tinta-fraca hover:text-foreground"
        >
          {detalhe ? "menos" : "etapas"}
          <span className={`inline-flex transition-transform ${detalhe ? "rotate-180" : ""}`}>
            <Icone de={ICONES.baixo} tamanho={14} />
          </span>
        </button>
      </div>
      {/* No celular a fileira de pastilhas JÁ é a lista escrita — abrir o detalhe
          por cima dela seria a mesma informação duas vezes, e o botão que o
          liga nem aparece ali (ele pode ter ficado ligado no computador). */}
      {detalhe && !celular && (
        <div id="detalhe-do-funil" className="mt-0.5 flex flex-col gap-[3px]">
          {segmentos.map((e) => {
            const cor = e.cor || corAvatar(e.chave);
            const marcada = etapaDaMesa === e.chave;
            const rostos = rostosPorEtapa.get(e.chave) ?? [];
            return (
              <div
                key={e.chave}
                className={`grid grid-cols-[6px_1fr_auto] items-center gap-2 rounded-controle px-1.5 py-[4px] ${
                  marcada ? "bg-foreground/8" : "hover:bg-foreground/4"
                } ${e.n ? "" : "opacity-50"}`}
              >
                <span className="h-[22px] w-[6px] rounded-full" style={{ background: cor }} />
                <button
                  onClick={() => escolherEtapa(e.chave)}
                  aria-pressed={marcada}
                  className="min-w-0 cursor-pointer rounded-etiqueta border-0 bg-transparent p-0 text-left"
                >
                  <span className="block truncate text-apoio font-semibold">{e.rotulo}</span>
                  <span className="mt-[3px] block h-[4px] overflow-hidden rounded-full bg-foreground/8">
                    <span
                      className="block h-full rounded-full"
                      style={{ width: `${Math.round((100 * e.n) / somaVisivel)}%`, background: cor }}
                    />
                  </span>
                </button>
                <span className="flex items-center">
                  <span className="flex">
                    {rostos.slice(0, 5).map((p, i) => (
                      <button
                        key={p.chave}
                        onClick={() => ajustar({ etapa: e.chave, resp: respDaMesa === p.chave && marcada ? null : p.chave })}
                        aria-label={`${p.nome} em ${e.rotulo}: ${p.n}`}
                        data-dica={`${p.nome} · ${e.rotulo}: ${p.n}`}
                        className={`cursor-pointer rounded-full border-2 bg-transparent p-0 ${i ? "-ml-[7px]" : ""} ${
                          marcada && respDaMesa === p.chave ? "border-foreground" : "border-secondary"
                        }`}
                      >
                        {p.chave === SEM_RESPONSAVEL ? (
                          <span className="block size-[20px] rounded-full border border-dashed border-terra/60" />
                        ) : (
                          <Avatar nome={p.nome} foto={p.foto} cor={p.cor} tamanho={20} />
                        )}
                      </button>
                    ))}
                  </span>
                  <span className="ml-1.5 min-w-[16px] text-right font-mono text-selo text-tinta-fraca tabular-nums">{e.n}</span>
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/*
       * As ETIQUETAS mais usadas, como filtro de um toque.
       *
       * É o recorte que o escritório inventou — e o que ele usa o dia inteiro
       * ("os urgentes", "quem tem audiência marcada"). Só as seis primeiras: a
       * lista vem ordenada por quantos casos cada uma marca, então o que sobra
       * é cauda longa, e ela mora no "+" da conversa, onde a etiqueta se põe.
       */}
      {(catalogoDeTags ?? []).length > 0 && (
        <div role="group" aria-label="Filtrar por etiqueta" className="mt-2.5 flex flex-wrap items-center gap-1.5">
          {catalogoDeTags.slice(0, 6).map((c) => {
            const marcada = tagDaMesa.toLowerCase() === c.tag.toLowerCase();
            const [fundo, letra] = tinta(c.tag, c.cor);
            return (
              <button
                key={c.tag}
                type="button"
                onClick={() => ajustar({ tag: marcada ? null : c.tag })}
                aria-pressed={marcada}
                data-dica={`${c.n} caso(s) com “${c.tag}”`}
                /* A medida da `Etiqueta` (24, recuo 9, 12/600, raio 6), porque é
                   ela que aparece na linha. A marcada leva o aro de 2px em
                   sombra, e não `ring`: o anel de foco zera o `ring` do registro
                   e a marca sumia justo quando o teclado chegava nela. */
                className={`flex h-6 cursor-pointer items-center gap-1.5 rounded-etiqueta border-none px-[9px] text-[12px] font-semibold transition-shadow ${
                  marcada
                    ? "shadow-[0_0_0_2px_var(--color-secondary),0_0_0_4px_var(--color-foreground)]"
                    : "hover:shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--color-foreground)_20%,transparent)]"
                }`}
                style={{ background: fundo, color: letra }}
              >
                {c.tag}
                <span className="font-mono tabular-nums opacity-65">{contagem(c.n)}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* O recorte, escrito. Recorte que age é recorte que se vê. */}
      {ativo && (
        <div className="mt-2.5 flex min-h-padrao items-center gap-1.5 rounded-controle bg-verde/8 px-2.5 py-1.5 text-corpo font-semibold text-verde">
          Mostrando: {respDaMesa ? nomeResp : "todos"} · {etapaDaMesa ? rotuloEtapa : "qualquer etapa"}
          {tagDaMesa ? ` · etiqueta “${tagDaMesa}”` : ""}
          {/* O criativo vem da tela de Criativos, e não tem controle aqui: a
              linha escrita é a única pista de que a lista está recortada. */}
          {criativoDaMesa ? ` · ${criativoDaMesa === SEM_CRIATIVO ? "sem criativo" : `criativo “${criativoDaMesa}”`}` : ""} ·{" "}
          {noRecorte}
          <button
            onClick={() => ajustar({ etapa: null, resp: null, tag: null, criativo: null })}
            aria-label="Limpar o recorte"
            className="alvo ml-auto flex cursor-pointer items-center rounded-etiqueta border-0 bg-transparent p-0 text-tinta-fraca hover:text-foreground"
          >
            <Icone de={ICONES.fechar} tamanho={14} />
          </button>
        </div>
      )}
    </div>
  );
}
